import React, { useState, useEffect } from 'react';
import { channelApi } from '../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FiRefreshCw, FiShoppingBag, FiGlobe, FiCheckCircle, FiActivity, FiSearch, FiFileText, FiMail, FiRotateCw } from 'react-icons/fi';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function TransactionMonitor() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ payment: {}, fulfillment: {}, revenue: 0 });
  const [total, setTotal] = useState(0);
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [flightForm, setFlightForm] = useState(null); // { bookingId, componentName, flt_type, ... }
  const [savingFlight, setSavingFlight] = useState(false);
  const [emailForm, setEmailForm] = useState(null);   // { bookingId, email }
  const [sendingEmail, setSendingEmail] = useState(false);
  const [retryingKey, setRetryingKey] = useState(null);
  const { user } = useAuth();

  const PAGE_SIZE = 20;
  // Sandbox is real data for the credential that produced it, but the monitor defaults
  // to production: 56 dummy bookings otherwise bury the ones under test, and the
  // revenue card becomes real money added to play money.
  const [filters, setFilters] = useState({
    env: 'production',
    channel_name: 'ALL',
    payment_status: '',
    fulfillment: '',
    booked_from: '',
    booked_to: '',
  });
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');

  // Components in these states are waiting on a schedule nobody has entered yet.
  const AWAITING_FLIGHT = ['Awaiting Flight Details', 'Blocked (Lead Time < H-1)'];

  const setFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };

  const openFlightForm = (tx, sup) => setFlightForm({
    bookingId: tx.id,
    componentName: sup.componentName,
    // Default to the route direction captured on the product at import time.
    flt_type: sup.flt_type || tx.flight_details?.flt_type || 'departure',
    departure_airline: tx.flight_details?.departure_airline || '',
    departure_flight_no: tx.flight_details?.departure_flight_no || '',
    departure_date: tx.flight_details?.departure_date || tx.travelDate || '',
    departure_time: tx.flight_details?.departure_time || '',
    arrival_airline: tx.flight_details?.arrival_airline || '',
    arrival_flight_no: tx.flight_details?.arrival_flight_no || '',
    arrival_date: tx.flight_details?.arrival_date || '',
    arrival_time: tx.flight_details?.arrival_time || '',
  });

  const submitFlightDetails = async () => {
    if (!flightForm) return;
    const { bookingId, componentName, ...payload } = flightForm;
    setSavingFlight(true);
    try {
      const res = await channelApi.post(`/admin/transactions/${bookingId}/flight-details`, payload);
      const n = res.data?.data?.redispatched ?? 0;
      // 0 re-dispatched is saved-but-not-sent, not a success — say which one happened.
      if (n > 0) toast.success(`Jadwal tersimpan, ${n} komponen dikirim ulang ke supplier.`);
      else toast.warning('Jadwal tersimpan, tapi tidak ada komponen yang dikirim ulang.');
      setFlightForm(null);
      fetchTransactions();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Gagal menyimpan jadwal penerbangan.');
    } finally {
      setSavingFlight(false);
    }
  };

  const retryComponent = async (tx, sup) => {
    const key = `${tx.id}::${sup.productId}`;
    setRetryingKey(key);
    try {
      const res = await channelApi.post(`/admin/transactions/${tx.id}/components/${sup.productId}/retry`);
      // The endpoint answers 200 whether the supplier accepted or refused again, and
      // says which in the message — a retry that failed again is not a success toast.
      const status = res.data?.data?.component?.status || '';
      if (/^Success/.test(status)) toast.success(res.data.message);
      else toast.warning(res.data?.message || 'Retry dijalankan, komponen masih gagal.');
      fetchTransactions();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Gagal menjalankan retry.');
    } finally {
      setRetryingKey(null);
    }
  };

  const submitResendEmail = async () => {
    if (!emailForm) return;
    setSendingEmail(true);
    try {
      const res = await channelApi.post(`/admin/transactions/${emailForm.bookingId}/resend-email`, { email: emailForm.email });
      toast.success(res.data?.message || 'E-ticket dikirim ulang.');
      setEmailForm(null);
    } catch (err) {
      // 502 = the send itself failed; the message carries the reason from the mailer.
      toast.error(err?.response?.data?.message || 'Gagal mengirim ulang e-ticket.');
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      // Every filter is a query param, not an Array.filter: hiding a sandbox row by not
      // rendering it still ships every passenger's NIK and email to the browser, and a
      // client-side search can only ever search the page already downloaded.
      const params = { page, limit: PAGE_SIZE };
      if (filters.env) params.env = filters.env;
      if (filters.channel_name !== 'ALL') params.channel_name = filters.channel_name;
      if (filters.payment_status) params.payment_status = filters.payment_status;
      if (filters.fulfillment) params.fulfillment = filters.fulfillment;
      if (filters.booked_from) params.booked_from = filters.booked_from;
      // Inclusive end date: booking_time is a timestamp, so a bare date would drop
      // everything booked after midnight on the last day the operator picked.
      if (filters.booked_to) params.booked_to = `${filters.booked_to} 23:59:59`;
      if (query) params.q = query;
      if (user?.admin_status === 'admin_member' && user?.organization_code) {
        params.member_code = user.organization_code.toLowerCase();
      }
      const response = await channelApi.get('/admin/transactions', { params });
      if (response.data?.status === 200) {
        setTransactions(response.data.data || []);
        setTotal(response.data.pagination?.total ?? (response.data.data || []).length);
        setSummary(response.data.summary || { payment: {}, fulfillment: {}, revenue: 0 });
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memuat log transaksi penjualan platform.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await channelApi.get('/admin/list');
      if (res.data?.status === 200) setChannels(res.data.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchChannels(); }, []);

  useEffect(() => {
    fetchTransactions();
  }, [page, filters, query, user?.organization_code, user?.admin_status]);

  // Typing is not a request: wait until the operator stops before hitting the server.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(searchTerm.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Card figures come from the server summary, which covers the whole filtered set.
  // Counting the twenty rows on screen answered a question nobody asked.
  const fulfilled = summary.fulfillment?.FULFILLED || 0;
  const troubled = (summary.fulfillment?.FAILED || 0) + (summary.fulfillment?.PARTIAL || 0);
  const successRate = total > 0 ? Math.round((fulfilled / total) * 100) : 100;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Channel buttons follow the env filter: a sandbox-only channel is not an option
  // while looking at production, and picking it would just empty the table.
  const channelNames = [...new Set(channels
    .filter((c) => !filters.env || !c.env || c.env === filters.env)
    .map((c) => c.channel_name).filter(Boolean))];

  const formatIDR = (num) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(num || 0);
  };

  /**
   * What a component's status means right now.
   *
   * A component keeps the wording of the moment it failed, so an entry the cronjob
   * later gave up on still reads "Failed (Queued for Retry)" while nothing is queued.
   * retry_state, joined on by the API from the live queue, wins over the stored text.
   */
  const describeStatus = (sup) => {
    const rs = sup.retry_state;
    if (rs && rs.status === 'dead_letter') {
      return { label: `Gagal — retry habis (${rs.retryCount}/${rs.maxRetries})`, tone: 'red', canRetry: true, detail: rs.lastError };
    }
    if (rs && rs.status === 'retrying') {
      return { label: `Menunggu retry (${rs.retryCount}/${rs.maxRetries})`, tone: 'amber', canRetry: true, detail: rs.lastError };
    }
    if (AWAITING_FLIGHT.includes(sup.status)) return { label: sup.status, tone: 'amber', canRetry: false };
    if (/Fail|Error|Dead/i.test(sup.status || '')) return { label: sup.status, tone: 'red', canRetry: !!rs };
    return { label: sup.status, tone: 'emerald', canRetry: false };
  };

  const TONE = {
    red: 'bg-red-500/10 text-red-600 hover:bg-red-500/15',
    amber: 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/15',
    emerald: 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15',
  };

  return (
    <div className="flex flex-col gap-6">

      {/* Header action bar */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <p className="mb-1 text-xs font-semibold text-slate-500">Product Manager / Monitor Transaksi API</p>
          <h1 className="text-2xl font-black tracking-tight text-slate-800">
            {user?.admin_status === 'admin_member'
              ? `Monitor Penjualan & Reservasi Tiket Member - ${String(user?.organization_code || '').toUpperCase()}`
              : 'Monitor Transaksi API'}
          </h1>
          <p className="text-sm text-slate-500">
            {user?.admin_status === 'admin_member'
              ? 'Pantau log reservasi tiket dan voucher dari paket bundling yang terintegrasi secara real-time.'
              : 'Pantau transaksi penjualan omnichannel, fulfillment, dan notifikasi supplier.'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchTransactions} className="gap-2 self-start md:self-auto">
          <FiRefreshCw className={isLoading ? 'animate-spin' : ''} />
          Refresh Penjualan
        </Button>
      </div>

      {/* Analytics widgets */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-[#ECFDF5] p-3 text-[#059669]">
                <FiShoppingBag className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {user?.admin_status === 'admin_member' ? 'Total Reservasi' : 'Total Penjualan API'}
                </p>
                <h3 className="text-2xl font-bold text-slate-800">{total} Pesanan</h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-[#EFF6FF] p-3 text-[#2563EB]">
                <FiGlobe className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {user?.admin_status === 'admin_member' ? 'Organisasi Member' : 'Perlu Tindakan'}
                </p>
                {/* "Channel teraktif" was a share of the rows on screen, which changes
                    with the page size. Bookings whose components failed or only half
                    succeeded is the number that decides whether anyone must act. */}
                <h3 className="text-lg font-bold text-slate-800 truncate max-w-[180px]">
                  {user?.admin_status === 'admin_member'
                    ? (user?.organization_name || String(user?.organization_code || '').toUpperCase())
                    : `${troubled} Booking`}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>

        {user?.admin_status !== 'admin_member' ? (
          <Card className="border-0 bg-white shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-amber-50 p-3 text-amber-600">
                  <FiActivity className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Omset Penjualan API</p>
                  <h3 className="text-xl font-black text-slate-800">
                    {formatIDR(summary.revenue)}
                  </h3>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 bg-white shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-amber-50 p-3 text-amber-600">
                  <FiFileText className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Hak Akses Isolasi</p>
                  <h3 className="text-lg font-bold text-slate-800">Tenancy Aktif</h3>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-green-50 p-3 text-green-600">
                <FiCheckCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {user?.admin_status === 'admin_member' ? 'Status Tiket API' : 'Notifikasi API Supplier'}
                </p>
                <h3 className="text-2xl font-bold text-slate-800">{successRate}% Sukses</h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Table Card */}
      <Card className="border-0 bg-white shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-base font-bold text-slate-700">Daftar Transaksi Channel Eksternal</CardTitle>

            <div className="flex flex-wrap items-center gap-3">
              {/* Search — sent to the server as ?q=, so it reaches every page and every
                  passenger name, not only the twenty rows already downloaded. */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Cari booking, invoice, paket, email, penumpang..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg border border-slate-200 pl-9 pr-4 py-1.5 text-xs focus:border-brand-500 focus:outline-none w-[260px]"
                />
              </div>

              {/* Environment — derived from the credential that made the booking, so
                  sandbox traffic is separable even though one server serves both. */}
              <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs">
                {[['production', 'Production'], ['sandbox', 'Sandbox'], ['', 'Semua env']].map(([val, label]) => (
                  <button
                    key={label}
                    onClick={() => setFilter({ env: val, channel_name: 'ALL' })}
                    className={`px-3 py-1 rounded-md font-semibold transition-all ${filters.env === val ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <select
                value={filters.payment_status}
                onChange={(e) => setFilter({ payment_status: e.target.value })}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-600 focus:border-brand-500 focus:outline-none"
              >
                <option value="">Semua pembayaran</option>
                {['PENDING', 'PAID', 'EXPIRED', 'CANCELLED', 'REFUNDED'].map((v) => (
                  <option key={v} value={v}>{v}{summary.payment?.[v] != null ? ` (${summary.payment[v]})` : ''}</option>
                ))}
              </select>

              <select
                value={filters.fulfillment}
                onChange={(e) => setFilter({ fulfillment: e.target.value })}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-600 focus:border-brand-500 focus:outline-none"
              >
                <option value="">Semua fulfillment</option>
                {['FULFILLED', 'PARTIAL', 'FAILED', 'PENDING'].map((v) => (
                  <option key={v} value={v}>{v}{summary.fulfillment?.[v] != null ? ` (${summary.fulfillment[v]})` : ''}</option>
                ))}
              </select>

              {/* Booking date range — native date inputs, no picker library. */}
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <input
                  type="date" value={filters.booked_from}
                  onChange={(e) => setFilter({ booked_from: e.target.value })}
                  className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none"
                />
                <span>s/d</span>
                <input
                  type="date" value={filters.booked_to}
                  onChange={(e) => setFilter({ booked_to: e.target.value })}
                  className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none"
                />
              </div>

              {/* Channel filter — dynamic, scoped to the selected env. */}
              <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs">
                <button
                  onClick={() => setFilter({ channel_name: 'ALL' })}
                  className={`px-3 py-1 rounded-md font-semibold transition-all ${filters.channel_name === 'ALL' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  Semua channel
                </button>
                {channelNames.map(ch => (
                  <button
                    key={ch}
                    onClick={() => setFilter({ channel_name: ch })}
                    className={`px-3 py-1 rounded-md font-semibold transition-all ${filters.channel_name === ch ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    {ch}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="font-bold w-[120px]">Kode Booking</TableHead>
                <TableHead className="font-bold">Mitra OTA</TableHead>
                <TableHead className="font-bold">Paket Liburan</TableHead>
                <TableHead className="font-bold">Tanggal Travel</TableHead>
                {user?.admin_status !== 'admin_member' && <TableHead className="font-bold">Total Pembayaran</TableHead>}
                <TableHead className="font-bold">Daftar Wisatawan</TableHead>
                <TableHead className="font-bold">
                  {user?.admin_status === 'admin_member' ? 'Reservasi Tiket/Voucher Anda' : 'Notifikasi Reservasi API Supplier'}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={user?.admin_status === 'admin_member' ? 6 : 7} className="py-8 text-center text-slate-400">Memuat data transaksi...</TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={user?.admin_status === 'admin_member' ? 6 : 7} className="py-8 text-center text-slate-400">Tidak ada transaksi yang cocok dengan filter ini.</TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => (
                  <TableRow key={tx.id} className="hover:bg-slate-50/50">
                    <TableCell className="align-top">
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-800">{tx.id}</span>
                        <span className="text-[10px] font-mono text-slate-400 mt-0.5">{tx.invoiceCode}</span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-700">
                          {tx.channel_name}
                          {/* Shown on every row, not only when filtering: a sandbox
                              booking must never be mistaken for a real sale. */}
                          {tx.env && (
                            <Badge className={`ml-1.5 text-[8px] h-4 px-1.5 align-middle ${
                              tx.env === 'sandbox'
                                ? 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/15'
                                : 'bg-slate-500/10 text-slate-500 hover:bg-slate-500/15'
                            }`}>
                              {tx.env}
                            </Badge>
                          )}
                        </span>
                        <span className="text-[9px] text-slate-400">
                          {new Date(tx.bookingTime).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top font-semibold text-slate-800 text-xs">
                      {tx.packageName}
                      <span className="block font-mono text-[9px] text-slate-400 mt-0.5">Kode: {tx.packageCode}</span>
                    </TableCell>
                    <TableCell className="align-top text-xs text-slate-600 font-medium">
                      {new Date(tx.travelDate).toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </TableCell>
                    {user?.admin_status !== 'admin_member' && (
                      <TableCell className="align-top font-black text-slate-800 text-xs">
                        {formatIDR(tx.total_price)}
                      </TableCell>
                    )}
                    <TableCell className="align-top">
                      <div className="flex flex-col gap-1">
                        {tx.passengers.map((p, i) => (
                          <span key={i} className="text-[11px] text-slate-600 font-semibold block">
                            • {p.name} <span className="text-[9px] font-mono text-slate-400">({p.identityNumber})</span>
                          </span>
                        ))}
                        {(tx.contact_email || tx.contact_phone) && (
                          <div className="mt-1.5 pt-1.5 border-t border-slate-100">
                            {tx.contact_email && (
                              <span className="text-[10px] text-slate-400 block">
                                ✉ {tx.contact_email}
                              </span>
                            )}
                            {tx.contact_phone && (
                              <span className="text-[10px] text-slate-400 block">
                                ✆ {tx.contact_phone}
                              </span>
                            )}
                            <Button
                              size="sm" variant="outline"
                              className="mt-1.5 h-6 gap-1 px-2 text-[9px]"
                              onClick={() => setEmailForm({ bookingId: tx.id, email: tx.contact_email || '' })}
                            >
                              <FiMail className="h-3 w-3" /> Kirim ulang e-ticket
                            </Button>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex flex-col gap-1.5">
                        {tx.supplier_notification_status && tx.supplier_notification_status.length > 0 ? (
                          tx.supplier_notification_status.map((sup, idx) => {
                            const st = describeStatus(sup);
                            const busy = retryingKey === `${tx.id}::${sup.productId}`;
                            return (
                            <div key={idx} className="flex flex-col border border-slate-100 rounded-lg p-1.5 bg-slate-50/50">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[10px] font-bold text-slate-600 truncate max-w-[120px]">{sup.componentName}</span>
                                <Badge className={`text-[9px] h-4 px-1.5 shrink-0 ${TONE[st.tone]}`}>
                                  {st.label}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-1 text-[9px] text-slate-400 mt-0.5">
                                <span className="font-medium">{sup.supplierName || 'Supplier'}</span>
                              </div>
                              {/* The reason it failed, verbatim — an operator deciding
                                  whether a retry is worth trying needs the error, not
                                  just the colour of the badge. */}
                              {st.detail && (
                                <span className="mt-0.5 text-[9px] text-red-500/80 break-words" title={st.detail}>
                                  {st.detail}
                                </span>
                              )}
                              {AWAITING_FLIGHT.includes(sup.status) ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-1 h-6 text-[9px] px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                                  onClick={() => openFlightForm(tx, sup)}
                                >
                                  Isi jadwal penerbangan
                                </Button>
                              ) : (
                                <div className="flex items-center justify-between text-[9px] text-slate-400 mt-1 font-mono">
                                  <span>Ref: {sup.voucherCode || '-'}</span>
                                  {sup.voucherCode && <span className="text-brand-500 font-bold">QR Issued</span>}
                                </div>
                              )}
                              {st.canRetry && (
                                <Button
                                  size="sm" variant="outline" disabled={busy}
                                  className="mt-1 h-6 gap-1 px-2 text-[9px] border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => retryComponent(tx, sup)}
                                >
                                  <FiRotateCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} />
                                  {busy ? 'Mengirim ulang...' : 'Retry sekarang'}
                                </Button>
                              )}
                            </div>
                            );
                          })
                        ) : (
                          <span className="text-xs text-slate-400 font-medium">Reservasi Non-Component</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Server-side paging: the browser never holds more than one page of
              passenger data, and the counts above still describe the whole set. */}
          <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
            <span>
              {total === 0
                ? 'Tidak ada data'
                : `Menampilkan ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} dari ${total} transaksi`}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1 || isLoading}
                onClick={() => setPage((n) => Math.max(1, n - 1))}>
                Sebelumnya
              </Button>
              <span className="font-semibold text-slate-600">{page} / {pageCount}</span>
              <Button variant="outline" size="sm" disabled={page >= pageCount || isLoading}
                onClick={() => setPage((n) => n + 1)}>
                Berikutnya
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Joumpa needs a fixed flight before IAS accepts the booking. GOERS has no
          place to collect one when the service is bought inside a package, so the
          operator fills it here and the held component is dispatched immediately. */}
      <Dialog open={!!flightForm} onOpenChange={(open) => !open && setFlightForm(null)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="text-base">Jadwal penerbangan — {flightForm?.componentName}</DialogTitle>
          </DialogHeader>

          {flightForm && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Jenis layanan</Label>
                <select
                  className="mt-1 w-full h-9 rounded-md border border-slate-200 px-2 text-sm"
                  value={flightForm.flt_type}
                  onChange={(e) => setFlightForm((f) => ({ ...f, flt_type: e.target.value }))}
                >
                  <option value="departure">Departure (keberangkatan)</option>
                  <option value="arrival">Arrival (kedatangan)</option>
                  <option value="transfer">Transfer (keduanya)</option>
                </select>
              </div>

              {/* IAS requires the departure half for departure and transfer. */}
              {(flightForm.flt_type === 'departure' || flightForm.flt_type === 'transfer') && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 text-[11px] font-semibold text-slate-500">Keberangkatan</div>
                  <div>
                    <Label className="text-xs">Maskapai</Label>
                    <Input className="mt-1 h-9" placeholder="GA" value={flightForm.departure_airline}
                      onChange={(e) => setFlightForm((f) => ({ ...f, departure_airline: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">No. penerbangan</Label>
                    <Input className="mt-1 h-9" placeholder="GA-204" value={flightForm.departure_flight_no}
                      onChange={(e) => setFlightForm((f) => ({ ...f, departure_flight_no: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Tanggal</Label>
                    <Input type="date" className="mt-1 h-9" value={flightForm.departure_date}
                      onChange={(e) => setFlightForm((f) => ({ ...f, departure_date: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Jam</Label>
                    <Input type="time" className="mt-1 h-9" value={flightForm.departure_time}
                      onChange={(e) => setFlightForm((f) => ({ ...f, departure_time: e.target.value }))} />
                  </div>
                </div>
              )}

              {(flightForm.flt_type === 'arrival' || flightForm.flt_type === 'transfer') && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 text-[11px] font-semibold text-slate-500">Kedatangan</div>
                  <div>
                    <Label className="text-xs">Maskapai</Label>
                    <Input className="mt-1 h-9" placeholder="GA" value={flightForm.arrival_airline}
                      onChange={(e) => setFlightForm((f) => ({ ...f, arrival_airline: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">No. penerbangan</Label>
                    <Input className="mt-1 h-9" placeholder="GA-205" value={flightForm.arrival_flight_no}
                      onChange={(e) => setFlightForm((f) => ({ ...f, arrival_flight_no: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Tanggal</Label>
                    <Input type="date" className="mt-1 h-9" value={flightForm.arrival_date}
                      onChange={(e) => setFlightForm((f) => ({ ...f, arrival_date: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Jam</Label>
                    <Input type="time" className="mt-1 h-9" value={flightForm.arrival_time}
                      onChange={(e) => setFlightForm((f) => ({ ...f, arrival_time: e.target.value }))} />
                  </div>
                </div>
              )}

              <p className="text-[11px] text-slate-400">
                IAS butuh jadwal minimal H-1. Komponen langsung dikirim ulang ke supplier setelah disimpan.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFlightForm(null)} disabled={savingFlight}>Batal</Button>
            <Button onClick={submitFlightDetails} disabled={savingFlight}>
              {savingFlight ? 'Menyimpan...' : 'Simpan & kirim'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-send the e-ticket. The address is editable so an operator can check the
          rendering on their own inbox, or correct a partner's typo, without that
          override being written back onto the booking. */}
      <Dialog open={!!emailForm} onOpenChange={(open) => !open && setEmailForm(null)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="text-base">Kirim ulang e-ticket — {emailForm?.bookingId}</DialogTitle>
          </DialogHeader>
          {emailForm && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Alamat tujuan</Label>
                <Input
                  className="mt-1 h-9" type="email" placeholder="nama@domain.com"
                  value={emailForm.email}
                  onChange={(e) => setEmailForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <p className="text-[11px] text-slate-400">
                Email berisi kredensial komponen yang sudah berhasil. Komponen yang masih
                gagal tidak akan punya QR/voucher di dalamnya.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailForm(null)} disabled={sendingEmail}>Batal</Button>
            <Button onClick={submitResendEmail} disabled={sendingEmail || !emailForm?.email}>
              {sendingEmail ? 'Mengirim...' : 'Kirim'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
